import { Chain } from "@rainbow-me/rainbowkit";
import { API_BASE_URL, API_VERSION, JAM_VERSION } from "../config/env";
import { IGlobalStore, SelectedToken } from "../store/global/GlobalStore";
import { Quote, QuoteTokenData } from "../types/quote";

import { TokenList } from "../store/global/getAllTokenLists";
import { isBestQuotePmm } from "./quoteUtils";
import { Address, formatUnits } from "viem";
import { orderStatusResponseSchema } from "../generated-schemas";
import { OrderStatusResponse } from "../types/orderStatus";
import { Amount } from "../types/amount";
import { AmountsReceived, Order } from "../types/order";
import { getTokenDecimalsByAddress, isAddressEqual } from "./tokenUtils";
import { CHAINS } from "config/chains";

export const isQuantityLargerThanBalance = (store: IGlobalStore) => {
  return store.takerTokens.some(token => {
    const balanceForToken = store.balances.find(balance =>
      isAddressEqual(balance.tokenAddress, token.tokenAddress)
    )?.balance.value;
    const inputtedQuantity = token.amount.value;
    return balanceForToken?.lt(inputtedQuantity);
  });
};

export const getPmmParamTypes = () => {
  return {
    EIP712Domain: [
      { name: "name", type: "string" },
      { name: "version", type: "string" },
      { name: "chainId", type: "uint256" },
      { name: "verifyingContract", type: "address" },
    ],
    Aggregate: [
      { name: "expiry", type: "uint256" },
      { name: "taker_address", type: "address" },
      { name: "maker_addresses", type: "address[]" },
      { name: "maker_nonces", type: "uint256[]" },
      { name: "taker_tokens", type: "address[][]" },
      { name: "maker_tokens", type: "address[][]" },
      { name: "taker_amounts", type: "uint256[][]" },
      { name: "maker_amounts", type: "uint256[][]" },
      { name: "receiver", type: "address" },
      { name: "commands", type: "bytes" },
    ],
  } as const;
};
export const getJamParamTypes = () => {
  return {
    EIP712Domain: [
      { name: "name", type: "string" },
      { name: "version", type: "string" },
      { name: "chainId", type: "uint256" },
      { name: "verifyingContract", type: "address" },
    ],
    JamOrder: [
      { name: "taker", type: "address" },
      { name: "receiver", type: "address" },
      { name: "expiry", type: "uint256" },
      { name: "nonce", type: "uint256" },
      { name: "executor", type: "address" },
      { name: "minFillPercent", type: "uint16" },
      { name: "hooksHash", type: "bytes32" },
      { name: "sellTokens", type: "address[]" },
      { name: "buyTokens", type: "address[]" },
      { name: "sellAmounts", type: "uint256[]" },
      { name: "buyAmounts", type: "uint256[]" },
      { name: "sellNFTIds", type: "uint256[]" },
      { name: "buyNFTIds", type: "uint256[]" },
      { name: "sellTokenTransfers", type: "bytes" },
      { name: "buyTokenTransfers", type: "bytes" },
    ],
  } as const;
};
export const getDomain = (chain: Chain, quote: Quote) => {
  return {
    name: isBestQuotePmm(quote.routeType) ? "BebopSettlement" : "JamSettlement",
    version: "1",
    chainId: Number(chain.id),
    verifyingContract: quote.settlementAddress,
  } as const;
};

export const getSendOrderEndpoint = (quote: Quote, chain: Chain) => {
  if (isBestQuotePmm(quote.routeType)) {
    return `${API_BASE_URL}/${CHAINS[chain.id].apiName}/${API_VERSION}/order`;
  } else {
    return `${API_BASE_URL}/jam/${CHAINS[chain.id].apiName}/${JAM_VERSION}/order`;
  }
};
export const getSignatureData = (signedData: string) => {
  const sig = "0x" + signedData.substring(2);
  return {
    signature: sig,
  };
};

export const areTokensIdentical = (a: Array<SelectedToken>, b: Array<SelectedToken>) => {
  const aSet = new Set(a.map(e => e.tokenAddress));
  const bSet = new Set(b.map(e => e.tokenAddress));
  if (aSet.size !== bSet.size) {
    return false;
  }
  for (const aEntry of aSet.keys()) {
    if (!bSet.has(aEntry)) {
      return false;
    }
  }
  return true;
};

export const transformAmountsReceived = (
  amountsReceived: OrderStatusResponse["amounts"],
  tokenList: TokenList | undefined
): AmountsReceived | null => {
  if (!amountsReceived) {
    return null;
  }
  return Object.fromEntries(
    Object.entries(amountsReceived).map(([address, value]) => {
      return [
        address,
        new Amount(
          formatUnits(BigInt(value ?? 0), getTokenDecimalsByAddress(address as Address, tokenList))
        ),
      ];
    })
  ) as AmountsReceived;
};

const getOrderStatusEndpoint = (chain: Chain, isPmmQuote: boolean, quoteId: string) => {
  const version = isPmmQuote ? API_VERSION : JAM_VERSION;
  const endpoint = isPmmQuote ? "" : "/jam";

  return `${API_BASE_URL}${endpoint}/${
    CHAINS[chain.id].apiName
  }/${version}/order-status?quote_id=${quoteId}`;
};

const fetchOrderStatus = async (
  quoteClone: Quote,
  chain: Chain,
  tokenList: TokenList | undefined,
  isPmmQuote: boolean
): Promise<Order | undefined> => {
  try {
    const response = await fetch(getOrderStatusEndpoint(chain, isPmmQuote, quoteClone.quoteId));
    if (response.ok) {
      const json = await response.json();
      const orderStatusResponse = orderStatusResponseSchema.safeParse(json);

      if (orderStatusResponse.success) {
        const txHash = orderStatusResponse.data.txHash;
        const amountsReceived = transformAmountsReceived(
          orderStatusResponse.data.amounts,
          tokenList
        );
        if (txHash) {
          return {
            txHash: txHash,
            amountsReceived: amountsReceived,
            status: "Success",
            expiry: quoteClone.expiry.value.toNumber(),
          } as Order;
        }
      } else {
        console.error(orderStatusResponse.error.format());
      }
    }
  } catch (e) {
    console.error(`Could not fetch order status`, e);
  }
  return undefined;
};
export const getOrderStatus = async (
  quoteClone: Quote,
  chain: Chain,
  tokenList: TokenList | undefined
): Promise<Order | undefined> => {
  const retryInterval = 1000; // 1 second in milliseconds
  const isPmmQuote = quoteClone.routeType === "PMM";
  const attemptFetch = async (): Promise<Order | undefined> => {
    const result = await fetchOrderStatus(quoteClone, chain, tokenList, isPmmQuote);

    if (result !== undefined) {
      return result;
    }
    await new Promise(resolve => setTimeout(resolve, retryInterval));
    return attemptFetch();
  };

  return attemptFetch();
};

export const calculateUsdTradeSurplus = (
  order: Order | undefined,
  quote: Quote | undefined
): Amount => {
  if (!order || !quote) {
    return Amount.ZERO;
  }
  const surplus = Object.values(quote.makerTokens).reduce(
    (totalSurplus: Amount, token: QuoteTokenData) => {
      const tokenSurplus = order.amountsReceived[token.contractAddress].sub(token.amount);
      return totalSurplus.add(tokenSurplus.mul(token.priceUsd ?? Amount.ZERO));
    },
    Amount.ZERO
  );

  return surplus;
};
